import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { oneDark } from "@codemirror/theme-one-dark";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { LivePodium } from "../components/LivePodium";
import { LiveLeaderboard } from "../components/LiveLeaderboard";
import { fmtSecs } from "../lib/liveTime";
import { clientHeaders } from "../lib/clientId";
import { TrackBadge } from "../components/TrackBadge";
import { TypingSprint } from "../components/live/TypingSprint";
import { QuestionCountdown } from "../components/live/QuestionCountdown";
import {
  Play, Send, Loader2, Terminal, Trophy, Clock, CheckCircle2,
  Radio, Medal, Home, WifiOff, User, Code2, UserX, RotateCcw, Keyboard,
} from "lucide-react";
import { isBlockMode } from "../lib/modes";
const BlocksWorkspace = lazy(() => import("../components/blocks/BlocksWorkspace").then((m) => ({ default: m.BlocksWorkspace })));
const BlocksSolutionView = lazy(() => import("../components/blocks/BlocksSolutionView").then((m) => ({ default: m.BlocksSolutionView })));
const NavWorkspace = lazy(() => import("../components/nav/NavWorkspace").then((m) => ({ default: m.NavWorkspace })));
const NavReplay = lazy(() => import("../components/nav/NavReplay").then((m) => ({ default: m.NavReplay })));

const isNavQuestion = (q) => !!q && (q.mode === "junior" || q.blocks?.kind === "nav");

const API = "/api";
const DEFAULT_CODE = "# Write your Python code here\n";

export default function LivePlayer() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [state, setState] = useState(null);
  const [ended, setEnded] = useState(null);
  const [connected, setConnected] = useState(false);
  const [code, setCode] = useState(DEFAULT_CODE);
  const [workspace, setWorkspace] = useState(null);  // Blocks track: block program behind `code`
  const [customInput, setCustomInput] = useState("");
  const [output, setOutput] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [navBusy, setNavBusy] = useState(false);  // Junior Blocks: keep the grid up while the run animates after submit
  const [kicked, setKicked] = useState(null);
  const [rejoining, setRejoining] = useState(false);
  const [typingOpen, setTypingOpen] = useState(false);  // Typing Sprint filler game while waiting after a correct answer
  const wsRef = useRef(null);
  const closedRef = useRef(false);
  const questionIdRef = useRef(null);
  const joinCodeRef = useRef(null);
  const lastVersionRef = useRef(null);
  const isDarkMode = document.documentElement.classList.contains("dark");

  useEffect(() => {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem("liveDrillSession"));
    } catch { /* ignore */ }
    if (!saved?.session_id || !saved?.participant_token) {
      navigate("/join");
      return;
    }
    setSession(saved);
  }, [navigate]);

  const connect = useCallback((saved) => {
    if (closedRef.current) return;
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(
      `${proto}://${window.location.host}/api/live/ws/${saved.session_id}?role=participant&token=${saved.participant_token}`
    );
    wsRef.current = ws;
    // A stalled handshake (busy network/ingress) would otherwise hang until the TCP timeout
    const handshakeTimer = setTimeout(() => { if (ws.readyState === WebSocket.CONNECTING) ws.close(); }, 8000);

    const onKicked = () => {
      closedRef.current = true;
      sessionStorage.removeItem("liveDrillSession");
      setKicked({ ...saved, join_code: joinCodeRef.current });
    };

    ws.onopen = () => { clearTimeout(handshakeTimer); setConnected(true); };
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === "session_state") {
        if (msg.state_version < (lastVersionRef.current ?? -1)) return; // stale snapshot
        lastVersionRef.current = msg.state_version;
        joinCodeRef.current = msg.join_code;
        setState(msg);
        const qId = msg.question?.question_id;
        if (msg.status === "question_active" && qId && qId !== questionIdRef.current) {
          questionIdRef.current = qId;
          setCode(DEFAULT_CODE);
          setWorkspace(null);
          setCustomInput(msg.question.example_input || "");
          setOutput("");
          setLastResult(null);
          setTypingOpen(false);
        }
      } else if (msg.type === "session_ended") {
        setEnded(msg);
        sessionStorage.removeItem("liveDrillSession");
        closedRef.current = true;
      } else if (msg.type === "kicked") {
        onKicked();
      } else if (msg.type === "idle_warning") {
        toast.warning(msg.message, { duration: 8000 });
      } else if (msg.type === "time_up") {
        toast.info("Time's up!");
      }
    };
    ws.onclose = (e) => {
      clearTimeout(handshakeTimer);
      setConnected(false);
      if (!closedRef.current && e.code !== 4404 && e.code !== 4403 && e.code !== 4401) {
        setTimeout(() => connect(saved), 2000);
      } else if (e.code === 4404) {
        setEnded({ reason: "not_found", leaderboard: [] });
        sessionStorage.removeItem("liveDrillSession");
        closedRef.current = true;
      } else if ((e.code === 4401 || e.code === 4403) && !closedRef.current) {
        onKicked();
      }
    };
  }, []);

  const rejoin = async () => {
    if (!kicked?.join_code) {
      navigate("/join");
      return;
    }
    setRejoining(true);
    try {
      const res = await fetch(`${API}/live/join/${kicked.join_code}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ participant_token: kicked.participant_token }),
      });
      const data = await res.json();
      if (res.ok) {
        const saved = {
          session_id: data.session_id,
          participant_id: data.participant_id,
          participant_token: data.participant_token,
          nickname: data.nickname,
          is_anonymous: data.is_anonymous,
        };
        sessionStorage.setItem("liveDrillSession", JSON.stringify(saved));
        setKicked(null);
        setState(null);
        setSession(saved);
        toast.success("You're back in!");
      } else {
        toast.error(data.detail || "Couldn't rejoin the session");
      }
    } catch {
      toast.error("Couldn't rejoin the session. Please try again.");
    } finally {
      setRejoining(false);
    }
  };

  useEffect(() => {
    if (!session) return;
    closedRef.current = false;
    lastVersionRef.current = null;
    connect(session);
    const ping = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ action: "ping" }));
      }
    }, 25000);
    return () => {
      closedRef.current = true;
      clearInterval(ping);
      wsRef.current?.close();
    };
  }, [session, connect]);

  // Each phase (question -> leaderboard -> next question) starts at the top of the page
  useEffect(() => {
    if (state?.status) window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [state?.status, state?.current_index]);

  const isBlocksQ = isBlockMode(state?.question?.mode);
  const isNavQ = isNavQuestion(state?.question);
  // Blocks rooms: warm up the Blockly chunks so the leaderboard showcase doesn't flash "Loading blocks…"
  useEffect(() => {
    if (isBlockMode(state?.mode)) {
      import("../components/blocks/BlocksSolutionView");
      import("../components/blocks/BlocksWorkspace");
    }
    if (state?.mode === "junior") {
      import("../components/nav/NavWorkspace");
      import("../components/nav/NavReplay");
    }
  }, [state?.mode]);

  const handleTest = async () => {
    setIsRunning(true);
    setOutput("");
    try {
      const res = await fetch(`${API}/code/test`, {
        method: "POST",
        headers: clientHeaders(),
        body: JSON.stringify({ code, custom_input: customInput, mode: isBlocksQ ? state.question.mode : "python" }),
      });
      const result = await res.json();
      if (result.success) {
        setOutput(result.output?.trim() || "(No output)");
      } else {
        setOutput(`Error: ${result.error || "Unknown error"}`);
      }
    } catch (e) {
      setOutput(`Error: ${e.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  // POST to /live/submit and handle the "class moved on" cases; resolves to the result or null when nothing should be shown
  const postLiveSubmit = async (payload) => {
    const qidAtSubmit = state?.question?.question_id;
    const res = await fetch(`${API}/live/submit`, {
      method: "POST",
      headers: clientHeaders(),
      body: JSON.stringify({ session_id: session.session_id, participant_token: session.participant_token, ...payload }),
    });
    const result = await res.json();
    if (!res.ok) {
      if (res.status === 400 && (result.detail || "").includes("No question is currently active")) {
        toast.info("The class has moved on — waiting for the next question.", { duration: 5000 });
      } else {
        toast.error(result.detail || "Submission failed");
      }
      return null;
    }
    if (result.already_answered) {
      toast.info(result.message || "You already solved this question!");
      return null;
    }
    // Teacher moved on (or ended) while grading - don't show a stale result
    if (result.question_closed || (qidAtSubmit && questionIdRef.current !== qidAtSubmit)) {
      if (result.session_ended) return null;
      if (result.correct && result.points_earned > 0) {
        toast.success(`Answer recorded! +${result.points_earned} pts — the class has moved to the next question.`, { duration: 6000 });
        window.dispatchEvent(new Event("refreshUserStats"));
      } else if (result.correct) {
        toast.success("Correct! The class has moved to the next question.", { duration: 5000 });
      } else {
        toast.info("The class has moved on to the next question.", { duration: 5000 });
      }
      return null;
    }
    setLastResult(result);
    if (result.correct) window.dispatchEvent(new Event("refreshUserStats"));
    return result;
  };

  // Junior Blocks: NavWorkspace grades through us as soon as Play reaches the treasure
  const submitNavProgram = async ({ code: navCode, workspace: navWorkspace }) => {
    try {
      return await postLiveSubmit({ code: navCode, workspace: navWorkspace });
    } catch (e) {
      toast.error(`Error: ${e.message}`);
      return null;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setOutput("");
    try {
      const result = await postLiveSubmit({ code, workspace: isBlocksQ ? workspace : undefined });
      if (!result) return;
      if (result.blocks_rule_error) {
        setOutput(`⚠️ ${result.message}`);
      } else if (result.correct) {
        const timeTxt = result.time_taken != null ? ` in ${fmtSecs(result.time_taken)}` : "";
        setOutput(`✓ Correct! Passed ${result.passed}/${result.total} test cases\n+${result.points_earned} points${timeTxt}${result.speed_bonus > 0 ? ` (includes +${result.speed_bonus} speed bonus!)` : ""}`);
      } else {
        let text = `✗ Not quite right — Passed ${result.passed}/${result.total} test cases`;
        if (result.hint) {
          if (result.hint.error) text += `\n\nError: ${result.hint.error}`;
          else if (result.hint.expected != null) text += `\n\nExpected: ${result.hint.expected}\nGot: ${result.hint.actual}`;
        }
        text += "\n\nKeep trying!";
        setOutput(text);
      }
    } catch (e) {
      setOutput(`Error: ${e.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const me = state?.participants?.find((p) => p.participant_id === session?.participant_id);
  const myRank = (ended?.leaderboard || state?.leaderboard || []).find(
    (e) => e.participant_id === session?.participant_id
  );

  // ===== Removed by teacher screen =====
  if (kicked) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="live-player-kicked">
        <div className="inline-flex p-3 rounded-2xl bg-red-100 dark:bg-red-900/30 mb-4">
          <UserX className="w-8 h-8 text-red-500" />
        </div>
        <h1 className="font-heading font-bold text-2xl sm:text-3xl mb-2">You've been removed</h1>
        <p className="text-muted-foreground text-sm mb-6">
          Your teacher removed you from this live session. If that was a mistake, ask them to restore you, then tap Rejoin.
        </p>
        <div className="flex flex-col gap-2">
          <Button className="w-full rounded-xl gap-2" onClick={rejoin} disabled={rejoining} data-testid="live-player-rejoin-btn">
            {rejoining ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Rejoin
          </Button>
          <Button variant="outline" className="w-full rounded-xl gap-2" onClick={() => navigate("/")} data-testid="live-player-kicked-home-btn">
            <Home className="w-4 h-4" /> Back to CodingDrills
          </Button>
        </div>
      </div>
    );
  }

  // ===== Session ended screen =====
  if (ended) {
    return (
      <div className="max-w-lg mx-auto px-4 py-10" data-testid="live-player-ended">
        <div className="text-center mb-4">
          <div className="inline-flex p-3 rounded-2xl bg-primary/10 mb-3">
            <Medal className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-heading font-bold text-3xl mb-1">Session Over!</h1>
          {ended.reason === "idle_timeout" && (
            <p className="text-sm text-muted-foreground">The session ended due to inactivity.</p>
          )}
          {myRank && (
            <p className="text-muted-foreground mt-2">
              You finished <strong className="text-foreground">#{myRank.rank}</strong> with{" "}
              <strong className="text-foreground">{myRank.score} points</strong>
            </p>
          )}
        </div>
        {ended.leaderboard?.length > 0 && (
          <LivePodium entries={ended.leaderboard} meId={session?.participant_id} />
        )}
        <Button className="w-full mt-6 gap-2 rounded-xl" onClick={() => navigate("/")} data-testid="live-player-home-btn">
          <Home className="w-4 h-4" />
          Back to CodingDrills
        </Button>
      </div>
    );
  }

  if (!session || !state) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="live-player-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Connecting to live session...</p>
      </div>
    );
  }

  return (
    <div className={`${isBlocksQ ? "max-w-6xl" : "max-w-4xl"} mx-auto px-4 py-6`} data-testid="live-player-page">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="relative flex h-3 w-3">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${connected ? "bg-green-400" : "bg-red-400"}`} />
            <span className={`relative inline-flex rounded-full h-3 w-3 ${connected ? "bg-green-500" : "bg-red-500"}`} />
          </span>
          <span className="font-heading font-semibold">{state.drill_title}</span>
          <TrackBadge mode={state.mode} testId="live-player-track-badge" />
        </div>
        <div className="flex items-center gap-2">
          {!connected && (
            <Badge variant="outline" className="gap-1 text-red-500 border-red-300">
              <WifiOff className="w-3 h-3" /> Reconnecting...
            </Badge>
          )}
          <Badge className="gap-1.5 bg-primary/10 text-primary border border-primary/30 hover:bg-primary/15 text-sm px-3 py-1" data-testid="live-player-nickname">
            <User className="w-3.5 h-3.5" />
            {session.nickname}
          </Badge>
          <Badge className="gap-1 bg-primary text-sm px-3 py-1" data-testid="live-player-score">
            <Trophy className="w-3 h-3" />
            {me?.score ?? 0} pts
          </Badge>
        </div>
      </div>

      {/* Lobby / waiting */}
      {state.status === "lobby" && (
        <div className="text-center py-16 bg-card rounded-2xl border border-border" data-testid="live-player-lobby">
          <div className="inline-flex p-4 rounded-full bg-primary/10 mb-4 animate-gentle-pulse">
            <Radio className="w-10 h-10 text-primary" />
          </div>
          <h2 className="font-heading font-bold text-2xl mb-2">You're in!</h2>
          <p className="text-muted-foreground mb-1">
            Playing as <strong className="text-primary" data-testid="live-player-lobby-nickname">{session.nickname}</strong>
          </p>
          <p className="text-muted-foreground">Waiting for your teacher to start the first question...</p>
          <p className="text-sm text-muted-foreground mt-4">
            {state.participants.length} player{state.participants.length !== 1 ? "s" : ""} joined
          </p>
        </div>
      )}

      {/* Active question but question data missing (safety fallback) */}
      {state.status === "question_active" && !state.question && (
        <div className="text-center py-16 bg-card rounded-2xl border border-border" data-testid="live-player-question-missing">
          <Clock className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h2 className="font-heading font-bold text-xl mb-1">Question unavailable</h2>
          <p className="text-sm text-muted-foreground">This question couldn't be loaded. Waiting for your teacher to move to the next one...</p>
        </div>
      )}

      {/* Active question */}
      {state.status === "question_active" && state.question && (
        <div className="space-y-5" data-testid="live-player-question">
          <div className="bg-card rounded-2xl border border-border p-6">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline">Question {state.current_index + 1} of {state.total_questions}</Badge>
                <QuestionCountdown remaining={state.question_time_remaining} limit={state.question_time_limit} testId="live-player-countdown" />
              </div>
              {me?.status === "correct" && (
                <Badge className="bg-green-500 gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Solved!
                </Badge>
              )}
            </div>
            <h2 className="font-heading font-bold text-xl sm:text-2xl mb-3" data-testid="live-player-question-title">
              {state.question.title}
            </h2>
            {isNavQ ? (
              <p className="text-sm text-muted-foreground" data-testid="live-player-nav-brief">Program the drill to collect every goal on the grid, press Play to test it, then Submit for Grading.</p>
            ) : (
              <>
            <p className="text-foreground leading-relaxed whitespace-pre-wrap mb-4">{state.question.description}</p>
            {state.question.input_format && (
              <div className="mb-3">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Input Format</h3>
                <p className="text-sm whitespace-pre-wrap">{state.question.input_format}</p>
              </div>
            )}
            {state.question.output_format && (
              <div className="mb-3">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Output Format</h3>
                <p className="text-sm whitespace-pre-wrap">{state.question.output_format}</p>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4 mt-3">
              {state.question.example_input && (
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Example Input:</span>
                  <div className="io-block">{state.question.example_input}</div>
                </div>
              )}
              <div>
                <span className="text-xs text-muted-foreground block mb-1">Example Output:</span>
                <div className="io-block">{state.question.example_output}</div>
              </div>
            </div>
              </>
            )}
          </div>

          {me?.status === "correct" && !navBusy ? (
            <div className="text-center py-10 bg-green-50 dark:bg-green-900/20 rounded-2xl border border-green-200 dark:border-green-800" data-testid="live-player-solved-waiting">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="font-heading font-bold text-xl text-green-700 dark:text-green-400">Nice work!</h3>
              {lastResult?.points_earned > 0 && (
                <p className="text-green-600 dark:text-green-400 mt-1" data-testid="live-player-result-points">
                  +{lastResult.points_earned} points{lastResult.time_taken != null ? ` in ${fmtSecs(lastResult.time_taken)}` : ""}{lastResult.speed_bonus > 0 ? ` (with +${lastResult.speed_bonus} speed bonus)` : ""}
                </p>
              )}
              <p className="text-sm text-muted-foreground mt-3 flex items-center justify-center gap-1.5">
                <Clock className="w-4 h-4" /> Waiting for your teacher to move on...
              </p>
              <div className="px-4 sm:px-8">
                {typingOpen ? (
                  <TypingSprint mode={state.mode} sessionId={session.session_id} token={session.participant_token} onClose={() => setTypingOpen(false)} />
                ) : (
                  <Button variant="outline" className="mt-5 gap-2 rounded-xl bg-background/70" onClick={() => setTypingOpen(true)} data-testid="typing-open-btn">
                    <Keyboard className="w-4 h-4" /> Play a typing game while you wait
                  </Button>
                )}
              </div>
            </div>
          ) : isNavQ ? (
            <Suspense fallback={<div className="h-[420px] rounded-xl border border-border flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading the drill…</div>}>
              <div data-testid="live-player-nav-editor">
                <NavWorkspace
                  key={state.question.question_id}
                  question={state.question}
                  storageId={`live_${session.session_id}_${state.question.question_id}`}
                  editorProps={{ submitProgram: submitNavProgram, onSubmitStart: () => setNavBusy(true), onSubmitEnd: () => setNavBusy(false), blocksHeight: 440, liveSessionId: session.session_id }}
                />
              </div>
            </Suspense>
          ) : (
            <div className="space-y-4">
              {isBlocksQ ? (
                <Suspense fallback={<div className="h-[420px] rounded-xl border border-border flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading blocks…</div>}>
                  <div data-testid="live-player-blocks-editor">
                    <BlocksWorkspace
                      key={state.question.question_id}
                      questionId={state.question.question_id}
                      storageId={`live_${session.session_id}_${state.question.question_id}`}
                      inputs={state.question.blocks?.inputs || []}
                      rules={state.question.blocks || null}
                      starterWorkspace={state.question.blocks?.starter_workspace || null}
                      height={420}
                      onChange={(py, ws) => { setCode(py); setWorkspace(ws); }}
                    />
                  </div>
                </Suspense>
              ) : (
                <div className="code-editor-wrapper" data-testid="live-player-editor">
                  <CodeMirror
                    value={code}
                    height="260px"
                    extensions={[python()]}
                    theme={isDarkMode ? oneDark : undefined}
                    onChange={(v) => setCode(v)}
                    className="text-sm"
                  />
                </div>
              )}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Terminal className="w-4 h-4" /> Custom Input
                </label>
                <Textarea
                  value={customInput}
                  onChange={(e) => setCustomInput(e.target.value)}
                  className="font-mono text-sm resize-none h-20"
                  data-testid="live-player-custom-input"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="outline" className="flex-1 gap-2 rounded-xl h-11" onClick={handleTest} disabled={isRunning || isSubmitting} data-testid="live-player-test-btn">
                  {isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Test Run
                </Button>
                <Button className="flex-1 gap-2 rounded-xl h-11 bg-secondary hover:bg-secondary/90" onClick={handleSubmit} disabled={isRunning || isSubmitting} data-testid="live-player-submit-btn">
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Submit Answer
                </Button>
              </div>
              {output && (
                <div className={`io-block ${output.startsWith("✓") ? "border-2 border-success bg-success/5" : output.startsWith("✗") ? "border-2 border-error bg-error/5" : ""}`} data-testid="live-player-output">
                  <pre className="whitespace-pre-wrap break-words overflow-auto">{output}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Leaderboard between questions */}
      {state.status === "leaderboard" && (
        <div className="space-y-4">
          <LiveLeaderboard
            entries={state.leaderboard}
            title="Leaderboard"
            meId={session?.participant_id}
            resetKey={`${state.session_id}-${state.current_index}`}
            testId="live-player-leaderboard"
          />
          {state.leaderboard_solution && (
            <div className="bg-card rounded-2xl border border-border p-5" data-testid="live-player-leaderboard-solution">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Code2 className="w-4 h-4 text-primary" />
                {state.leaderboard_solution.source === "player"
                  ? <>Correct solution by <span className="text-primary">{state.leaderboard_solution.nickname}</span></>
                  : "Sample Solution"}
              </h3>
              {state.leaderboard_solution.nav_grid ? (
                <Suspense fallback={<div className="p-3 text-sm text-muted-foreground">Loading the drill…</div>}>
                  <NavReplay grid={state.leaderboard_solution.nav_grid} run={state.leaderboard_solution.nav_run} workspace={state.leaderboard_solution.workspace} python={state.leaderboard_solution.code} testId="live-player-leaderboard-nav" />
                </Suspense>
              ) : isBlockMode(state.leaderboard_solution.mode) && state.leaderboard_solution.workspace ? (
                <Suspense fallback={<div className="p-3 text-sm text-muted-foreground">Loading blocks…</div>}>
                  <BlocksSolutionView workspace={state.leaderboard_solution.workspace} inputs={state.leaderboard_solution.inputs || []} python={state.leaderboard_solution.code} testId="live-player-leaderboard-blocks" />
                </Suspense>
              ) : (
                <pre className="io-block whitespace-pre-wrap">{state.leaderboard_solution.code}</pre>
              )}
            </div>
          )}
          <p className="text-center text-sm text-muted-foreground flex items-center justify-center gap-1.5">
            <Clock className="w-4 h-4" /> Waiting for the next question...
          </p>
        </div>
      )}

      {/* All questions done */}
      {state.status === "finished" && (
        <div className="space-y-4 max-w-lg mx-auto" data-testid="live-player-finished">
          <div className="text-center py-2">
            <Medal className="w-12 h-12 text-amber-500 mx-auto mb-2" />
            <h2 className="font-heading font-bold text-2xl">That's a wrap!</h2>
            {myRank && (
              <p className="text-muted-foreground mt-1">
                You finished <strong className="text-foreground">#{myRank.rank}</strong> with{" "}
                <strong className="text-foreground">{myRank.score} points</strong>
              </p>
            )}
          </div>
          <LivePodium entries={state.leaderboard} meId={session?.participant_id} />
        </div>
      )}
    </div>
  );
}
